import { sleep } from 'app-lib/asyncUtils'
import errorUtils from 'app-lib/errorUtils'
import _ from 'lodash'
import * as mobx from 'mobx'
import { BigNumber } from '@0x/utils'
import { chainConfigs, ChainId } from '../config/appConfig'
import RootStore from './RootStore'
import { TransactionType } from '../domain/txTypes'

type TransactionProp = 'hash' | 'gasAmount' | 'generator' | 'type' | 'error' | 'finished'
export type TransactionStatus = 'canceled' | 'failed' | 'success' | 'mining' | 'gas-check' | 'sent'

/**
 * Run and track a transaction state.
 *
 * example:
 * ```ts
 * const tx = new Transaction({ type, generator, chainId })
 * await tx.run()
 * ```
 */
export class Transaction {
  public chainId: ChainId = 1
  public error: (Error & { noError: boolean; stackMessages: string; title: string }) | null = null
  public finished = false
  public gasAmount = new BigNumber(0)
  public generator: AsyncGenerator<any>
  public hash = ''
  public time = Date.now()
  public type: TransactionType

  public get status(): TransactionStatus {
    if (this.error) {
      return this.error.noError ? 'canceled' : 'failed'
    }
    if (this.finished) {
      return 'success'
    }
    if (this.hash) {
      return 'mining'
    }
    if (this.gasAmount.isZero()) {
      return 'gas-check'
    }
    return 'sent'
  }

  public get success() {
    return this.status === 'success'
  }

  public get scannerLink() {
    const { blockExplorer } = chainConfigs[this.chainId as ChainId].chainDetails
    return `${blockExplorer}/tx/${this.hash}`
  }

  /**
   * Helper to set values through mobx actions.
   */
  public set(prop: TransactionProp, value: any) {
    ;(this[prop] as any) = value
  }

  /**
   * Helper to assign multiple props values through a mobx action.
   */
  public assign(props: { [key: string]: any }) {
    Object.assign(this, props)
  }

  /**
   * Run and updates the transaction state after each step (gas, sending, failure,...)
   * Error is handled.
   */
  public async run() {
    try {
      for await (const value of this.generator) {
        this.assign(value)
      }
      // Add artificial delay for polygon because of RPC; this raise the probability to get the data updated from the blockchain
      await sleep(this.chainId === 137 ? 30000 : 2000)
      return this
    } catch (err) {
      errorUtils.decorateError(err, { stackMessages: true })
      this.set('error', err)
      return this
    } finally {
      this.set('finished', true)
    }
  }

  constructor(props: {
    type: TransactionType
    chainId: ChainId
    hash?: string
    message?: string
    generator: AsyncGenerator<any>
  }) {
    this.type = props.type
    this.generator = props.generator
    this.chainId = props.chainId
    if (props.hash) {
      this.hash = props.hash
    }

    mobx.makeAutoObservable(this, undefined, { deep: false, autoBind: true })
  }
}

type TransactionStoreProp = 'transactions' | 'collapsed'

export default class TransactionStore {
  public transactions: Transaction[] = []
  public rootStore: RootStore

  public collapsed = false // TODO: remove collapsed from here when we have a UI store / AppVM

  public get isFinished() {
    return this.transactions.every(
      (tx) => tx.status === 'success' || tx.status === 'canceled' || tx.status === 'failed'
    )
  }

  /**
   * Helper to set values through mobx actions.
   */
  public set(prop: TransactionStoreProp, value: any) {
    ;(this[prop] as any) = value
  }

  /**
   * Helper to assign multiple props values through a mobx action.
   */
  public assign(props: { [key: string]: any }) {
    Object.assign(this, props)
  }

  public toggle(prop: TransactionStoreProp) {
    ;(this[prop] as any) = !this[prop]
  }

  public removeTx(tx: Transaction) {
    this.set('transactions', _.without(this.transactions, tx))
  }

  public async run(type: TransactionType, generator: AsyncGenerator<any>) {
    const { chainId } = this.rootStore.web3Connection
    if (!chainId) {
      throw new Error('Wrong chainId')
    }
    const tx = new Transaction({ type, generator, chainId })
    this.assign({ transactions: [tx].concat(this.transactions), collapsed: false })
    await tx.run()

    if (tx.status === 'canceled') {
      this.removeTx(tx)
    }
    return tx
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    mobx.makeAutoObservable(this, { rootStore: false }, { autoBind: true, deep: false })
  }
}
